import {ptlEnvs} from "@/tra/tancis/ptli/comn";
import {api} from "@/comn";
import {comnUtils} from "@/comn/utils";
import {TFormSchema, TGridSchema} from "@/comn/hooks";


/**
 * PtliRevwService
 * ----------------
 * - Contains constants, API calls, and form/grid schemas for customer reviews.
 *
 * BASE:
 * - Defines base path, API URL, and navigation nodes.
 *
 * URLS:
 * - Predefined page URLs for list (PTLI0707012Q) and detail (PTLI0707013Q) views.
 *
 * APIS:
 * - getCstmrRevwList: fetch paginated customer review list
 * - getRevwPrcss: fetch review process detail by ID
 * - saveRevwPrcss: create/save a review process
 * - deleteRevwPrcss: delete a review process by ID
 * - getDashCnt: fetch dashboard total counts
 * - getMonthStatistic: fetch monthly review statistics
 * - getBsopClsfCnt: fetch business classification counts
 *
 * SF_REVW_SRCH:
 * - Form schema for search/filtering reviews by process ID, name, classification, and user ID.
 *
 * SG_REVW_LIST:
 * - Grid schema to display review list including review ID, process name, classification, user ID, and registration date.
 *
 * SF_REVW_DTL_FORM:
 * - Form schema for review detail view, showing user ID, process name, review comments, tags, rating string, and classification.
 */


// Base configuration: defines paths, API base URL, and navigation nodes
export const BASE = {
    path: `${ptlEnvs.base}/revw/cstmr`,
    api: `${process.env.REACT_APP_API_PTLI}/api/v1/ptli/revw/cstmr`,
    nodes: [
        { path: "/", label: "L_LBL_MGMT" },
        { path: "/ptl/revw", label: "L_LBL_MGMT" },
    ],
};

// Predefined URLs for specific queries
export const URLS = {
    ptli0707012q: `${BASE.path}/ptli0707012q`,
    ptli0707013q: `${BASE.path}/ptli0707013q`,
};

// API functions for CRUD operations + statistics
export const APIS = {

    // Fetch paginated list of customer reviews
    getCstmrRevwList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },

    // Get review process detail by ID
    getRevwPrcss: (id: any) => {
        return api.get(`${BASE.api}/${id}`);
    },

    // Save new review process
    saveRevwPrcss: (data: any) => {
        return api.post(`${BASE.api}`, {
            ...data,
        });
    },

    // Delete review process by ID
    deleteRevwPrcss: (id: any) => {
        return api.delete(`${BASE.api}/${id}`);
    },

    // Get dashboard total counts
    getDashCnt:() => {
        return api.get(`${BASE.api}/cnt`);
    },

    // Get monthly statistics
    getMonthStatistic: () => {
        return api.get(`${BASE.api}/monthly`);
    },

    // Get business classification count
    getBsopClsfCnt: () => {
        return api.get(`${BASE.api}/bsop`);
    }
};

// Search form schema for filtering reviews
export const SF_REVW_SRCH: TFormSchema = {
    id: "form_RevwSrch",
    schema: {
        revwPrcssId: { type: "text", label: "L_REVWPRCSS_ID", letterCase: "upper" },
        revwPrcssNm: { type: "text", label: "L_REVWPRCSS_NM" },
        bsopClsfCd: { type: "select", label: "L_BSOP_CLSF_CD", all: true, area: "comnCd", comnCd: "COM0024" },
        usrId: {type: "text", label: "L_SBMT_USR_ID"},
    },
};

// Grid schema to display list of customer reviews
export const SG_REVW_LIST: TGridSchema = {
    id: "grid_RevwList",
    options: { pagination: "out", checkbox: false, index: true },
    head: [
        { cells: [{ header: "L_REVW_ID", binding: "cstmrRevwUid", width: "20%" }] },
        { cells: [{ header: "L_REFF_KEY_NO", binding: "trgtReff", width: "20%" }] },
        { cells: [{ header: "L_REVWPRCSS_NM", binding: "usrFdbckPrcssNm", width: "*" }] },
        { cells: [{ header: "L_BSOP_CLSF_CD", binding: "bsopClsfCd", width: "10%" }] },
        { cells: [{ header: "L_SBMT_USR_ID", binding: "usrId", width: "10%" }] },
        { cells: [{ header: "L_FRST_RGSR_DTM", binding: "frstRgsrDtm", width: "15%" }] },
    ],
    body: [
        { cells: [{ binding: "cstmrRevwUid" }] },
        { cells: [{ binding: "trgtReff" }] },
        { cells: [{ binding: "usrFdbckPrcssNm" }] },
        { cells: [{ binding: "bsopClsfCd" }] },
        { cells: [{ binding: "usrId" }] },
        { cells: [{ binding: "frstRgsrDtm", type: "datetime" }] },
    ],
};

// Detail form schema for showing/editing a single review record
export const SF_REVW_DTL_FORM: TFormSchema = {
    id: "form_RevwDtl",
    schema: {
        usrId: { type: "text", label: "L_SBMT_USR_ID", letterCase: "upper" },
        revwPrcssNm: { type: "text", label: "L_REVWPRCSS_NM" },
        revwCmnt: { type: "text", label: "L_CMNT_CN" },
        revwTags: { type: "text", label: "L_REVW_TG" },
        revwStr: { type: "text", label: "L_REVW_STR" },
        trgtReff: { type: "text", label: "L_REFF_KEY_NO" },
        bsopClsfCd: { type: "text", label: "L_BSOP_CLSF_CD" },
    },
};

export {}
